/* eslint-disable quote-props, no-param-reassign, no-shadow */
import d3 from 'd3';
import { hexbin } from 'd3-hexbin';
import * as Config from 'es6/components/memory-bank/config';
import BaseVisual from 'es6/components/memory-bank/visuals/base-visual';
import ProgressLayout from 'es6/components/memory-bank/layouts/progress';
import { ReadyToStudyCap } from 'es6/models/memory';

// If nodes are stacked, have a limit of the max number shown.
const MaxBadgeCount = 99;

// How long the user has to hover back over a node during a delay.
const HoverGracePeriod = 300;

// Offset for expanding nodes.
const MaxExpansionOffset = Config.Circle.RADIUS * 3 / 2;

// The progress visual is on a hexbin. So, each nodes radius is an
// apothem of the hexagon. Convert the cirle's radius to a hexagon
// radius.
const HexRadius = Config.Circle.RADIUS / Math.cos(Math.PI / 6);

// Padding for the sections. Essentially, make sure nothing
// falls on a hexbin across the lines.
const Padding = HexRadius * 2;

// Adjusters are used to add padding at places on the graph where overlap
// is undesireable.
const CheckpointAdjusters = [0.01, 0.02];
const ActivationAdjuster = 0.01;

// Class for hiding nodes (to prevent scaling/aliasing issues).
const HiddenClass = 'lower-neighbor-hidden';

// Maximum time to perform animation on update.
const MaxTransitionDelay = 50;
const MaxTransitionTime = 1000;

/**
 * Progress visual for the memory bank.
 */
export class Progress extends BaseVisual {
  constructor(...args) {
    super(...args);

    // Set up labels.
    this._layout = new ProgressLayout(this._memoryBankPlot.layoutsContainer);

    // Get the sections to create the scaling methods.
    const sections = this._layout.sections;
    const readySection = sections.ready;
    const notReadySection = sections.notReady;
    const checkpoint1Section = sections.checkpoint1;
    const checkpoint2Section = sections.checkpoint2;
    const sessionItemsCountSection = sections.sessionItemsCount;

    // Create some scale functions to determine where the nodes should be placed.
    // The x scale uses the sum of progress and getScaledProgress() for the domain.
    // The y scale uses the currentRetention attribute for the domain.
    const scale = {
      x: d3.scale
        .linear()
        .domain([
          0,
          80,
          80 + CheckpointAdjusters[0],
          100 + CheckpointAdjusters[0],
          100 + CheckpointAdjusters[1],
          120 + CheckpointAdjusters[1],
        ])
        .range([
          readySection.x + Padding,
          checkpoint1Section.x - Padding,
          checkpoint1Section.x + Padding,
          checkpoint2Section.x - Padding,
          checkpoint2Section.x + Padding,
          checkpoint2Section.x + checkpoint2Section.width - Padding,
        ])
        .clamp(true),

      y: d3.scale
        .linear()
        .domain([0, ReadyToStudyCap, ReadyToStudyCap + ActivationAdjuster, 1 + ActivationAdjuster])
        .range([
          readySection.y + readySection.height - Padding,
          readySection.y + Padding,
          notReadySection.y + notReadySection.height - Padding,
          notReadySection.y + Padding,
        ])
        .clamp(true),
    };

    // Each node gets put into hexbins, so the points are centered
    // and consistent.
    this._hexbin = hexbin()
      .radius(HexRadius)
      .x(d => {
        let progress = d.getScaledProgress();

        if (d.isAtCheckpoint2()) {
          progress += CheckpointAdjusters[1];
        } else if (d.isAtCheckpoint1()) {
          progress += CheckpointAdjusters[0];
        }

        return scale.x(progress);
      })
      .y(d => scale.y(!d.isReady() ? d.currentRetention + ActivationAdjuster : d.currentRetention));

    // The session item count just shows a number, put the text
    // in the center of the item circle.
    this._sessionItemsCountCoords = {
      x: sessionItemsCountSection.x + (sessionItemsCountSection.width / 2),
      y: sessionItemsCountSection.y + (sessionItemsCountSection.height / 2),
    };

    // Sorted list of nodes at a particular location.
    // The key is deteremined by the nodes getPositionKey() method.
    this._nodesAtPosition = {};
  }

  /**
   * Called at the end of the transition. Applies the appropriate
   * classes and events to each node.
   */
  _afterTransition() {
    // Keep a reference to use inside the methods.
    const handleMouseover = this._handleMouseover.bind(this);
    const handleMouseout = this._handleMouseout.bind(this);

    // Bind onmouseover and onmouseout methods. To not have
    // conflicts with the events bound by the tooltips, use namespace
    // in the event.
    //
    // https://github.com/mbostock/d3/wiki/Selections#on
    this._memoryBankPlot.selection.on({
      'mouseover.progress'(d) {
        handleMouseover(d, this);
      },
      'mouseout.progress'(d) {
        handleMouseout(d, this);
      },
    });

    // Remove all badge counts before updating.
    this._removeBadgeCount();

    // Count nodes that are stacked at each location. If stacked nodes
    // contain nodes that are in session, use the number of in session
    // nodes, instead.
    Object.keys(this._nodesAtPosition).forEach(lookupKey => {
      const nodes = this._nodesAtPosition[lookupKey];
      let length = Math.min(this._nodesAtPosition[lookupKey].length, MaxBadgeCount);

      // If there are items in session, replace the length with that value.
      const inSessionNodesCount = nodes.filter(n => n.inSession).length;
      if (inSessionNodesCount > 0) {
        length = inSessionNodesCount;
      }

      // If there's a length more than one, have it show on the top node
      // Show a number of how many items are beneath it.
      if (length > 1) {
        this._getSelection(nodes.slice(0, 1))
          .select('.node-group-count-badge')
          .text(length)
          .attr({
            'data-count': length,
            'transform': `translate(0, ${Config.Text.OFFSET})`,
          });
      }

      // Hide all child nodes.
      this._getSelection(nodes.slice(1)).classed(HiddenClass, true);
    });

    this._setTooltips();
  }

  /**
   * Calculates the node position and sets the target position on the
   * node. Also sets up the hash to decide what nodes are in the
   * same location.
   */
  _beforeTransition() {
    this._cleanup();

    // Set up this._nodesAtPosition and set up the target
    // for the node.
    this._getVisible()
      .each(this._setNodePosition.bind(this))
      .sort(this._sort)
      .each(d => {
        const lookupKey = d.getPositionKey();

        // Record the nodes at the target position.
        if (!this._nodesAtPosition[lookupKey]) {
          this._nodesAtPosition[lookupKey] = [];
        }

        // This array should keep the data in order of top to
        // bottom. So, the array needs to be inverted.
        this._nodesAtPosition[lookupKey].unshift(d);
      });

    // Get the number of new in session and add it to the layout.
    const inSessionNewNodesCount = this._memoryBankPlot.selection.filter(d => d.isNew() && d.inSession).size();
    this._layout.setSessionItemsCountLabel(inSessionNewNodesCount);
  }

  /**
   * Removes text and node from classes that might not be relevant on
   * transition.
   */
  _cleanup() {
    this._nodesAtPosition = {};

    this._memoryBankPlot.selection
      .classed(HiddenClass, false)
      .on({ '.progress': null });
  }

  /**
   * Collapses a series of nodes at the position of the corresponding
   * node.
   *
   * @param {MemoryBankNode} node
   */
  _collapseNodeGroup(node) {
    const lookupKey = node.getPositionKey();
    const nodes = this._nodesAtPosition[lookupKey];
    const selection = this._getSelection(nodes);
    const topNode = nodes[0];

    // If there are any nodes still hovered, don't collapse.
    if (nodes.filter(d => d.isHovered).length === 0) {
      selection.transition()
        .duration(Config.Animation.FAST)
        .attr('transform', d => this._translate(d.x, d.y))

        // Make sure the nodes are hidden again at the end.
        .each('end', function (d) {
          if (d !== topNode) {
            d3.select(this).classed(HiddenClass, true);
          }
        });
    }
  }

  /**
   * Expands a series of nodes at the position of the corresponding
   * node.
   *
   * @param {MemoryBankNode} node
   */
  _expandNodeGroup(node) {
    const nodesAtSamePosition = this._nodesAtPosition[node.getPositionKey()];
    const numberOfNodes = nodesAtSamePosition.length;

    // Calculate some values for seeing how far to stretch.
    // If there are more nodes than can fit using MaxExpansionOffset,
    // squish the nodes so they fit.
    let direction = 1;      // 1 for left, -1 for right
    let totalSpace = Config.ViewBox.WIDTH - node.x - Padding;

    // If in the strong areas, show to the left
    if (node.isAtCheckpoint1()) {
      direction = -1;
      totalSpace = node.x - this._layout.sections.ready.x - Padding;
    }

    const offset = (numberOfNodes * MaxExpansionOffset) > totalSpace ?
      totalSpace / numberOfNodes :
      MaxExpansionOffset;

    // Move all nodes that are not being expanded to the back.
    // The reason to do this, as opposed to moving the expanding nodes,
    // is to prevent mouseover events from acting strange. This has
    // the best cross browser behavior.
    this._getVisible()
      .filter(d => d.getPositionKey() !== node.getPositionKey())
      .each(this._moveToBack);

    // Filter nodes in this position.
    this._getVisible()
      .filter(d => d.getPositionKey() === node.getPositionKey())
      .classed(HiddenClass, false)
      .transition()
      .duration(Config.Animation.MEDIUM)
      .attr('transform', (d, i) => {
        // Because the nodes are actually in reverse order on the DOM,
        // the index has to be inverted.
        const index = numberOfNodes - 1 - i;
        const nodeOffset = index * offset * direction;
        return this._translate(d.x + nodeOffset, d.y);
      });
  }

  /**
   * Callback for onmouseover.
   *
   * @param {MemoryBankNode} node
   * @param {HTMLElement} el  <g> tag for node
   */
  _handleMouseover(node, el) {
    node.isHovered = true;

    // Expand the radius a little bit.
    d3.select(el)
      .select('circle')
      .transition()
      .duration(Config.Animation.SLOW)
      .ease('elastic')
      .attr('r', Config.Circle.RADIUS_HOVER);

    this._expandNodeGroup(node);
  }

  /**
   * Callback for onmouseout.
   *
   * @param {MemoryBankNode} node
   * @param {HTMLElement} el  <g> tag for node
   */
  _handleMouseout(node, el) {
    node.isHovered = false;

    // Shrink the radius.
    d3.select(el)
      .select('circle')
      .transition()
      .duration(Config.Animation.SLOW)
      .ease('elastic')
      .attr('r', Config.Circle.RADIUS);

    // If the user hovered over another element in the collection,
    // don't collapse it yet. Let execution finish.
    setTimeout(this._collapseNodeGroup.bind(this, node), HoverGracePeriod);
  }

  /**
   * Unbind the expanding events and remove all the progress related
   * classes from the nodes.
   */
  hide() {
    super.hide();
    this._layout.hide();
    this._cleanup();

    // Remove badge count from all nodes.
    this._removeBadgeCount();
  }

  /**
   * Moves a node to the top of the svg, making it appear behind
   * all the other nodes. This is necessary because SVG does not
   * support z-index.
   *
   * @this HTMLElement the g element for the node
   * @param {MemoryBankNode} d
   * @param {Number} i the index
   */
  _moveToBack(d, i) {
    const parentNode = this.parentNode;

    // Insert before the first child.
    return parentNode.insertBefore(this, parentNode.childNodes[i]);
  }

  /**
   * Removes badge count from the specified selection.
   *
   * @param {d3_selection} selection if null, will use all node data.
   */
  _removeBadgeCount(selection = null) {
    selection = selection || this._memoryBankPlot.selection;

    selection.selectAll('.node-group-count-badge')
      .text(null)
      .attr({ 'data-count': 0, 'transform': null });
  }

  /**
   * Sets the x, y coordinates for a node.
   *
   * @param {MemoryBankNode} node
   */
  _setNodePosition(node) {
    // Take the scaled values and put them into the appropriate hexbin.
    const hexbinCoordinates = this._hexbin([node]);

    // Unfortunately, some browsers will round the values, so to keep
    // the node values consistent with the elements, do some rounding.
    node.x = Math.round(hexbinCoordinates[0].x);
    node.y = Math.round(hexbinCoordinates[0].y);
  }

  /**
   * Called when a visual is either initially rendered or transitioning
   * from another visual. The inheriting class should define what happens
   * to the updated nodes.
   */
  show() {
    super.show();
    this._layout.show();

    // Maintained for checking the end of the transition.
    let length = this._getVisible().length;

    this._beforeTransition();

    this._getVisible()
      .transition()
      .duration(Config.Animation.SLOWEST)
      .delay(node => Math.random() * 200)
      .attr('transform', d => this._translate(d.x, d.y))
      .each('end', d => {
        // Check to see if the transition has ended.
        length--;
        if (length === 0) {
          this._afterTransition();
        }
      });

    // Move hidden nodes during the transition.
    this._moveHiddenNodes();
  }

  /**
   * Sort method for comparing two nodes together.
   *
   * @param {MemoryBankNode} nodeA
   * @param {MemoryBankNode} nodeB
   * @return {number}
   */
  _sort(nodeA, nodeB) {
    return d3.ascending(nodeA.inSession, nodeB.inSession) ||
      d3.descending(nodeA.shortTermMemory, nodeB.shortTermMemory) ||
      d3.descending(nodeA.progress, nodeB.progress) ||
      d3.descending(nodeA.currentRetention, nodeB.currentRetention);
  }

  /**
   * Called when a visual is being updated. In this case, the nodes may
   * be changing position.
   */
  update() {
    super.update();

    // Keep reference to the translate method.
    const translate = this._translate;

    // Run the before transition methods.
    this._beforeTransition();

    // If nodes enter the dataset on update, simply render them in place.
    this._getVisible()
      .filter(function () { return d3.select(this).classed('entered'); })
      .attr('transform', d => translate(d.x, d.y));

    // Set the current location of the node on the MemoryBankNode.
    this._getVisible().each(function (d) {
      const transform = d3.transform(d3.select(this).attr('transform'));
      const [x, y] = transform.translate;
      d.current = { x, y };
    });

    // Start by moving all the nodes that were new
    // to the center of the session items label.
    this._getVisible()
      .filter(function (d) { return d3.select(this).classed('was-new'); })
      .each(d => {
        d.current = {
          x: this._sessionItemsCountCoords.x,
          y: this._sessionItemsCountCoords.y,
        };
      })
      .attr('transform', d => translate(d.current.x, d.current.y));

    // Get all nodes that have moved.
    const moveSelection = this._getVisible().filter(d => d.x !== d.current.x || d.y !== d.current.y);
    let untransitionedNodesCount = moveSelection.size();

    // Move any hidden nodes if there are any.
    this._moveHiddenNodes();

    // If there are no nodes that have moved, just call afterTransition and
    // return.
    if (untransitionedNodesCount === 0) {
      this._afterTransition();
      return;
    }

    // Used to help decide delay on node. If amount of nodes being transitioned
    // will take longer than the maximum time to transition, evenly divide that
    // time by the maximum transition time (so it doesn't feel slow to the user).
    const delayIncrement =
      (MaxTransitionDelay * untransitionedNodesCount) > MaxTransitionTime ?
        Math.floor(MaxTransitionTime / untransitionedNodesCount) :
        MaxTransitionDelay;

    // Remove the badge count from all moving nodes.
    this._removeBadgeCount(moveSelection);

    // Perform a transition on all the nodes.
    moveSelection.transition()
      .duration(Config.Animation.SLOWEST)

      // Set a short delay based on where the node is sequentially.
      .delay((n, i) => i * delayIncrement)

      // Do not bind this function, the this keyword reflects
      // the element associated with the node.
      .tween('curveTween', function (d) {
        const el = this;

        // Keep a record of the original x and y position.
        const x = d.current.x;
        const y = d.current.y;
        const dx = d.x - x;
        const dy = d.y - y;

        const easeInSine = (t, b, c, d) => -c * Math.cos(t / d * (Math.PI / 2)) + c + b;
        const easeOutSine = (t, b, c, d) => c * Math.sin(t / d * (Math.PI / 2)) + b;

        return t => {
          const rx = easeInSine(t, 0, 1, 1);
          const ry = easeOutSine(t, 0, 1, 1);

          let moveToX = x + dx * rx;
          let moveToY = y + dy * ry;

          // The time is up, just set the nodes to the appropriate position.
          if (t === 1) {
            moveToX = d.x;
            moveToY = d.y;
          }

          d3.select(el).attr('transform', d => translate(moveToX, moveToY));
        };
      })

      // Check to see if the transition has ended.
      .each('end', d => {
        untransitionedNodesCount--;

        if (untransitionedNodesCount === 0) {
          this._afterTransition();
        }
      });
  }
}

export default Progress;
